import Phaser from 'phaser';
import { t } from '../i18n';
import { SANS, UI, Panel, uiButton } from '../render/theme';
import { _resolveAd, type AdKind } from '../ads';
import { Progress } from '../progress';

/**
 * MOCK ad overlay — a stand-in for a real rewarded/interstitial unit so the
 * whole economy is testable without an ad SDK. Rewarded units enforce a short
 * watch before the reward unlocks; interstitials are dismissable after a beat.
 * Closing a rewarded ad early returns "not completed" (no reward), matching how
 * real rewarded ads behave.
 */
export class AdScene extends Phaser.Scene {
  /** Android back button action, read by back-button.ts. */
  onBack?: () => void;
  private kind: AdKind = 'interstitial';
  private done = false;

  constructor() {
    super('Ad');
  }

  init(data: { kind: AdKind }) {
    this.kind = data.kind ?? 'interstitial';
    this.done = false;
  }

  create() {
    const { width, height } = this.scale;
    const rewarded = this.kind === 'rewarded';
    const watchSecs = rewarded ? 5 : 3;
    const M = 72;

    this.add.rectangle(0, 0, width, height, 0x05060d, 1).setOrigin(0).setInteractive();

    // Always-visible label so the screen is honestly marked as an ad.
    this.add.text(M, 96, t('ad_label'), {
      fontFamily: SANS, fontSize: '24px', color: UI.textDim, fontStyle: '700',
    }).setOrigin(0, 0.5);

    // Close target: a full size button, not a glyph. Early close on a rewarded ad = no reward.
    const closeBtn = this.add.text(width - M, 96, '✕', {
      fontFamily: SANS, fontSize: '44px', color: UI.textDim, fontStyle: '700',
      padding: { x: 28, y: 28 },
    }).setOrigin(1, 0.5).setInteractive({ useHandCursor: true });
    closeBtn.on('pointerdown', () => this.finish(false));
    this.onBack = () => this.finish(false);

    // House card
    const cardW = width - M * 2, cardH = Math.min(height * 0.4, 760);
    const cardY = height * 0.42;
    const panel = new Panel(this, M, cardY - cardH / 2, cardW, cardH, {
      fill: 0x0b1226, fillAlpha: 1, stroke: UI.hot, strokeAlpha: 0.5,
    });
    void panel;
    this.add.text(width / 2, cardY - 40, 'NØDUS', {
      fontFamily: SANS, fontSize: '110px', color: UI.text, fontStyle: '800',
    }).setOrigin(0.5);
    this.add.text(width / 2, cardY + 70, t('ad_house_tagline'), {
      fontFamily: SANS, fontSize: '30px', color: UI.textDim,
      align: 'center', wordWrap: { width: cardW - 120 },
    }).setOrigin(0.5);

    const headline = this.add.text(width / 2, height * 0.7,
      rewarded ? t('ad_rewarded_watching') : t('ad_interstitial_watching'), {
        fontFamily: SANS, fontSize: '34px', color: UI.text, fontStyle: '700',
        align: 'center', wordWrap: { width: width - 160 },
      }).setOrigin(0.5);

    // Progress bar that fills over the watch time
    const barW = width - M * 2, barY = height * 0.78;
    const track = this.add.graphics();
    track.fillStyle(UI.hairline, 1);
    track.fillRoundedRect(M, barY - 6, barW, 12, 6);
    const fill = this.add.graphics();
    const prog = { v: 0 };
    this.tweens.add({
      targets: prog, v: 1, duration: watchSecs * 1000, ease: 'Linear',
      onUpdate: () => {
        fill.clear();
        fill.fillStyle(rewarded ? UI.signal : UI.accent, 1);
        fill.fillRoundedRect(M, barY - 6, Math.max(12, barW * prog.v), 12, 6);
      },
    });

    const actionY = height * 0.87;
    let remaining = watchSecs;
    const counter = this.add.text(width / 2, actionY, this.countdownLabel(remaining), {
      fontFamily: SANS, fontSize: '30px', color: UI.accentHex, fontStyle: '700',
    }).setOrigin(0.5);

    const tick = () => {
      remaining -= 1;
      if (remaining > 0) {
        counter.setText(this.countdownLabel(remaining));
        return;
      }
      counter.destroy();
      headline.setText(rewarded ? t('ad_rewarded_done') : t('ad_interstitial_done'));
      uiButton(this, width / 2, actionY, barW, 124,
        rewarded ? t('ad_claim') : t('ad_continue'),
        rewarded ? UI.signal : UI.accent, () => this.finish(true), { primary: true, size: 36 });
    };
    this.time.addEvent({ delay: 1000, repeat: watchSecs - 1, callback: tick });
  }

  private countdownLabel(n: number): string {
    return t('ad_reward_in').replace('{n}', String(n));
  }

  private finish(completed: boolean) {
    if (this.done) return;
    this.done = true;
    if (completed) Progress.recordAd();
    _resolveAd(completed);
    this.scene.stop();
  }
}
